import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Group,
  NumberInput,
  Paper,
  Radio,
  Stack,
  Text,
  Textarea
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useCreateOperationalReturnOrder } from '../../hooks/orders/mutations';
import { IOrder } from '../../interfaces/Order';
import { IOrderProduct } from '../../interfaces/Product';
import { buildOrderFolio } from '../../utils/ordersUtils';
import { formatMoneyString, getCustomerFullName } from '../../utils/strings';
import { showNotification } from '../../utils/Notification/Notification';
import {
  buildCreateReturnOrderDto,
  getInitialOperationalReturnQuantity,
  getOperationalReturnNominalUnitPrice,
  OperationalReturnValidationError
} from '../../pages/NewOrder/utils/operationalReturns';
import { useGetStoreById } from '../../hooks/stores/queries';
import { useOrderPaymentLinesModal } from '../../hooks/useOrderPaymentLinesModal';
import OrderCreatedDrawer from '../OrderCreatedDrawer';
import OrderPaymentLinesModal from '../OrderModals/OrderPaymentLinesModal';
import { mergeCreatedOrderUpdate } from '../../pages/NewOrder/utils/orderCreatedUpdate';
import { resolvePersistedSourceLineId } from '../../pages/NewOrder/utils/lineIdentity';
import { useGetSections } from '../../hooks/sections/queries';
import { useGetOrderById } from '../../hooks/orders/queries';

type OperationalReturnDrawerProps = {
  opened: boolean;
  onClose: () => void;
  sourceOrder: IOrder;
};

const getEntityId = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && '_id' in value) {
    return String((value as { _id?: string })._id ?? '');
  }
  return '';
};

const describeLineOptions = (line: IOrderProduct): string => {
  const selectedOption = line.selectedOption?.label;
  const properties = Array.isArray(line.properties)
    ? line.properties.filter(Boolean).join(', ')
    : '';
  return [selectedOption, properties].filter(Boolean).join(' · ');
};

const resolveLineSectionName = (
  line: IOrderProduct,
  sectionNamesById: Map<string, string>
): string => {
  const storeProduct =
    line.storeProductId && typeof line.storeProductId === 'object'
      ? line.storeProductId
      : undefined;
  const section = line.section ?? storeProduct?.section;

  if (section && typeof section === 'object') {
    const populatedName = String(section.name ?? '').trim();
    if (populatedName) return populatedName;
  }

  return sectionNamesById.get(getEntityId(section)) ?? '';
};

export default function OperationalReturnDrawer({
  opened,
  onClose,
  sourceOrder
}: OperationalReturnDrawerProps) {
  const { t } = useTranslation(['NewOrder', 'CommonButtons']);
  const history = useHistory();
  const createReturn = useCreateOperationalReturnOrder();
  const [createdReturnOrder, setCreatedReturnOrder] = useState<IOrder | null>(null);
  const [createdReturnDrawerOpened, createdReturnDrawerHandlers] = useDisclosure(false);
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});
  const [returnItemsChargeMode, setReturnItemsChargeMode] =
    useState<'no_charge' | 'charge'>('no_charge');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const storeId =
    getEntityId(createdReturnOrder?.createdIn) || getEntityId(sourceOrder.createdIn);
  const { data: store, isSuccess: isStorePolicyLoaded } = useGetStoreById(
    Boolean(storeId) && (opened || Boolean(createdReturnOrder)),
    storeId
  );
  const { refetch: refetchCreatedReturnOrder } = useGetOrderById(
    createdReturnOrder?._id ?? '',
    undefined,
    { enabled: false, retry: false }
  );
  const { data: sections = [] } = useGetSections(Boolean(storeId) && opened, { storeId });
  const sectionNamesById = useMemo(
    () => new Map(sections.map((section) => [String(section._id ?? ''), section.name])),
    [sections]
  );
  const paymentLinesModal = useOrderPaymentLinesModal({ store });

  const sourceLines = useMemo(
    () =>
      [...(sourceOrder.products ?? []), ...(sourceOrder.buyAndGetProducts ?? [])].filter(
        (line): line is IOrderProduct =>
          typeof line === 'object' && Boolean(resolvePersistedSourceLineId(line))
      ),
    [sourceOrder.buyAndGetProducts, sourceOrder.products]
  );

  useEffect(() => {
    if (!opened) return;
    setSelectedQuantities({});
    setReturnItemsChargeMode('no_charge');
    setReason('');
    setNotes('');
    setError('');
  }, [opened, sourceOrder._id]);

  const setLineSelected = (line: IOrderProduct, selected: boolean) => {
    const lineId = resolvePersistedSourceLineId(line);
    setSelectedQuantities((current) => {
      const next = { ...current };
      const initialQuantity = getInitialOperationalReturnQuantity(line);
      if (selected && initialQuantity !== undefined) next[lineId] = initialQuantity;
      else delete next[lineId];
      return next;
    });
    setError('');
  };

  const handleCreate = async () => {
    try {
      setError('');
      const data = buildCreateReturnOrderDto({
        sourceLines,
        selectedQuantities,
        returnItemsChargeMode,
        reason,
        notes
      });
      const response = await createReturn.mutateAsync({
        sourceOrderId: sourceOrder._id || '',
        data
      });
      const returnOrder = response?.data?.data;
      if (!returnOrder?._id) throw new Error(t('NewOrder:operationalReturns.createError'));

      showNotification({
        type: 'Default',
        title: t('NewOrder:operationalReturns.created')
      });
      setCreatedReturnOrder(returnOrder);
      createdReturnDrawerHandlers.open();
      onClose();
    } catch (creationError: any) {
      if (creationError instanceof OperationalReturnValidationError) {
        setError(t(`NewOrder:operationalReturns.validation.${creationError.code}`));
        return;
      }
      const backendMessage = creationError?.response?.data?.errors?.[0];
      const message =
        (typeof backendMessage === 'string' && backendMessage) ||
        creationError?.message ||
        t('NewOrder:operationalReturns.createError');
      setError(message);
    }
  };

  const customer = typeof sourceOrder.customer === 'object' ? sourceOrder.customer : undefined;
  const customerName = customer
    ? getCustomerFullName(customer.name, customer.lastName)
    : '';
  const folio = buildOrderFolio(sourceOrder.folioPrefix, sourceOrder.sequence);

  const closeCreatedReturnDrawer = () => {
    createdReturnDrawerHandlers.close();
    setCreatedReturnOrder(null);
  };

  const handleCollectPayment = (orderId: string) => {
    if (
      !createdReturnOrder ||
      createdReturnOrder._id !== orderId ||
      !isStorePolicyLoaded ||
      !store
    ) return;

    paymentLinesModal.open(createdReturnOrder).then((paymentModalOpened) => {
      if (paymentModalOpened) closeCreatedReturnDrawer();
    });
  };

  const syncCreatedReturnAfterPayment = async () => {
    const result = await refetchCreatedReturnOrder();
    const refreshedOrder = result.data;
    if (!refreshedOrder?._id) {
      paymentLinesModal.close();
      return;
    }

    setCreatedReturnOrder(refreshedOrder);
    paymentLinesModal.syncOrder(refreshedOrder);
  };

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        position="right"
        size="lg"
        title={t('NewOrder:operationalReturns.drawerTitle')}
        closeOnClickOutside={!createReturn.isPending}
        closeOnEscape={!createReturn.isPending}>
        <Stack gap="md">
        <Stack gap={2}>
          <Text fw={600}>
            {t('NewOrder:operationalReturns.sourceOrder', { folio })}
          </Text>
          {customerName ? <Text size="sm">{customerName}</Text> : null}
          <Text size="sm" c="dimmed">
            {t('NewOrder:operationalReturns.sourceContextHelp')}
          </Text>
        </Stack>

        <Divider />

        <Stack gap="sm">
          {sourceLines.map((line) => {
            const lineId = resolvePersistedSourceLineId(line);
            const selected = Object.prototype.hasOwnProperty.call(selectedQuantities, lineId);
            const availableQuantity = Number(line.quantity ?? line.qty ?? 0);
            const optionDescription = describeLineOptions(line);
            const sectionName = resolveLineSectionName(line, sectionNamesById);
            const nominalPrice = getOperationalReturnNominalUnitPrice(
              line,
              Boolean(sourceOrder.express)
            );
            return (
              <Paper key={lineId} withBorder p="sm">
                <Stack gap="xs">
                  <Checkbox
                    checked={selected}
                    onChange={(event) => setLineSelected(line, event.currentTarget.checked)}
                    label={line.name}
                  />
                  {sectionName ? (
                    <Text size="xs" c="dimmed" fw={500}>
                      {sectionName}
                    </Text>
                  ) : null}
                  {optionDescription ? (
                    <Text size="xs" c="dimmed">{optionDescription}</Text>
                  ) : null}
                  <Group justify="space-between" align="end">
                    <Stack gap={0}>
                      <Text size="xs" c="dimmed">
                        {t('NewOrder:operationalReturns.availableQuantity', {
                          quantity: availableQuantity
                        })}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {t('NewOrder:operationalReturns.nominalUnitPrice', {
                          price: formatMoneyString(nominalPrice)
                        })}
                      </Text>
                    </Stack>
                    <NumberInput
                      aria-label={t('NewOrder:operationalReturns.returnQuantityFor', {
                        product: line.name
                      })}
                      disabled={!selected}
                      min={line.type === 'normal' || !line.type ? 1 : 0.001}
                      max={availableQuantity}
                      step={line.type === 'normal' || !line.type ? 1 : 0.001}
                      allowDecimal
                      clampBehavior="none"
                      value={selectedQuantities[lineId] ?? 0}
                      onChange={(value) =>
                        setSelectedQuantities((current) => ({
                          ...current,
                          [lineId]: Number(value)
                        }))
                      }
                      w={130}
                    />
                  </Group>
                </Stack>
              </Paper>
            );
          })}
        </Stack>

        <Radio.Group
          value={returnItemsChargeMode}
          onChange={(value) => setReturnItemsChargeMode(value as 'no_charge' | 'charge')}
          label={t('NewOrder:operationalReturns.chargeMode')}>
          <Stack mt="xs" gap="xs">
            <Radio value="no_charge" label={t('NewOrder:operationalReturns.noCharge')} />
            <Radio value="charge" label={t('NewOrder:operationalReturns.chargeAgain')} />
          </Stack>
        </Radio.Group>
        <Text size="sm" c="dimmed">
          {t('NewOrder:operationalReturns.chargeModeHelp')}
        </Text>

        <Textarea
          label={t('NewOrder:operationalReturns.reason')}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          autosize
          minRows={2}
        />
        <Textarea
          label={t('NewOrder:operationalReturns.notes')}
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
          autosize
          minRows={2}
        />

        {error ? <Alert color="red">{error}</Alert> : null}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={createReturn.isPending}>
            {t('CommonButtons:cancel')}
          </Button>
          <Button onClick={handleCreate} loading={createReturn.isPending}>
            {t('NewOrder:operationalReturns.create')}
          </Button>
        </Group>
        </Stack>
      </Drawer>
      {createdReturnOrder ? (
        <OrderCreatedDrawer
          opened={createdReturnDrawerOpened}
          onClose={closeCreatedReturnDrawer}
          order={createdReturnOrder}
          store={store || null}
          onStartNewOrder={() => {
            closeCreatedReturnDrawer();
            history.push(`/new-order?storeId=${storeId}`);
          }}
          onViewOrder={(orderId) => {
            closeCreatedReturnDrawer();
            history.push(`/orders/${orderId}?storeId=${storeId}`);
          }}
          onCollectPayment={isStorePolicyLoaded && store ? handleCollectPayment : undefined}
          onOrderUpdated={(updatedOrder) => {
            setCreatedReturnOrder((currentOrder) =>
              mergeCreatedOrderUpdate(currentOrder, updatedOrder)
            );
          }}
        />
      ) : null}
      {paymentLinesModal.opened ? (
        <OrderPaymentLinesModal
          onAddPaymentLine={syncCreatedReturnAfterPayment}
          title={paymentLinesModal.title}
          close={paymentLinesModal.close}
          opened={paymentLinesModal.opened}
          order={paymentLinesModal.order}
          onDeletePaymentLine={syncCreatedReturnAfterPayment}
          onUpdatePaymentLine={syncCreatedReturnAfterPayment}
          pinUser={paymentLinesModal.pinUser}
        />
      ) : null}
    </>
  );
}
